import {Vex, Dot} from "vexflow";
import VexFlowInteraction from "./VexFlowInteraction";
import { getNote, getGraceNote } from "../helpers/score";
import { measureAccidentals, parsePitch, KEY_SIGNATURES } from "./piano";
import { baseOf, FAMILIES, inRange, instrumentOf, partName, staffView } from "./instruments";
import { endingSpan, keyAt, JUMPS, MARKS, TEMPO_CHANGES } from "./score-structure";
import { takesOf, takeShown, nextTake } from "./takes";
import { NOTE_HIGHLIGHT_COLOR, timeSigs } from "../consts/score";
import _ from "lodash";
import {
  ACCENT,
  FLAM,
  DIDDLE,
  CHEESE,
  BUZZ,
  LEFT_STICKING,
  RIGHT_STICKING,
  STACCATO,
  TENUTO,
  MARCATO,
  FERMATA,
  DOWN_BOW,
  UP_BOW,
  OPEN,
  STOPPED,
} from "../consts/ornaments";

const VF = Vex.Flow;
const BASE_STAVE_SPACE = 125;
let STAVE_SPACE = BASE_STAVE_SPACE;
// Dynamics and hairpins sit on a line under the stickings, so a score with any gets this much
// more room beneath each staff (and between a part and the next).
const DYNAMICS_SPACE = 30;
// Every dynamic sits on one baseline this far below that line, and hairpins are centered on its
// lowercase letters (about 5px above the baseline).
const DYNAMIC_BASELINE = 20, HAIRPIN_HEIGHT = 9, HAIRPIN_MIDDLE = DYNAMIC_BASELINE - 5;
let PART_SPACE = BASE_STAVE_SPACE, LYRICS_BELOW_DYNAMICS = false;
// A large score packs its staffs closer (a single-line staff closer still), and shows a silent bar
// as one whole rest. The top of each staff within a row.
const LARGE_SCORE_PARTS = 5, LARGE_PART_SPACE = 92, LARGE_LINE_SPACE = 66, LARGE_ROW_MARGIN = 36, LARGE_DYNAMICS_SPACE = 16;
// Lyrics take a line of their own, under the dynamics.
const LYRICS_SPACE = 24, LARGE_LYRICS_SPACE = 22, LYRIC_LINE = 24;
let PART_TOPS = [];
// Room for the part names before a row: [the first row, later rows], whose names are abbreviated.
let LABEL_WIDTHS = [0, 0];
// Extra room above the first row when a nested tuplet's raised outer bracket needs it.
const NESTED_TUPLET_HEADROOM = 12;
let STAVE_TOP = 0;
// A bar's takes (src/lib/takes.js): which one it shows ("Take 1 of 5"), and the next one drawn
// smaller above it as an ossia, so a score with takes gets this much more room over each row. Set
// for each score drawn: the bars with takes found while drawing, and what clicking a badge does.
const OSSIA_SPACE = 84, OSSIA_SCALE = 0.6, SVG_NS = "http://www.w3.org/2000/svg";
let TAKES = null;
// The last click on a bar (which bar, when), across redraws, to count two quick ones as a double-click.
// A press anywhere else in between starts over, so two clicks on a bar with something else done
// between them aren't one.
// Pressing a note can redraw what's under the mouse (a bar's whole rest becomes its beats), so the
// click that ends the press lands on the bar instead: `notePressed` keeps that from selecting the bar.
let lastBarClick = { index: null, at: 0 }, watchingPresses = false, notePressed = false;
function watchPresses() {
  if (watchingPresses || typeof document === "undefined") return;
  watchingPresses = true;
  document.addEventListener("pointerdown", (event) => { notePressed = false; if (!event.target.closest?.("[data-measure-index]")) lastBarClick = { index: null, at: 0 }; }, true);
}
const PADDING = 50;
const FORMAT_PADDING = 13;
const MIN_BAR_SIZE = 100;
const SCORE_MIN_WIDTH = 100;
const TUPLET_ADJACENT_BRACKET_INSET = 4;
const TUPLET_BRACKET_TICK_HEIGHT = 8;
const ACCENTED_TUPLET_NOTEWARD_OFFSET = 5;
const UNACCENTED_TUPLET_NOTEWARD_OFFSET = -5;
const FIRST_NOTE_FORMAT_X = 8;
const MEASURE_RIGHT_EDGE_GUARD = 6;
const TICK_CONTEXT_MIN_GAP = 2;
const LONGEST_UNBEAMED_TUPLET_DURATION = 4;
const MEASURE_NUMBER_FONT_SIZE = 15;
const MEASURE_NUMBER_GAP = 6;
// Space between the bottom of the staff (or its lowest note) and the stickings.
const STICKING_GAP = 2;

export function initialize(id) {
  // Create an SVG renderer and attach it to the DIV element named "vf".
  const renderer = new VF.Renderer(
    document.getElementById(id),
    VF.Renderer.Backends.SVG
  );
  // Configure the rendering context.

  const context = renderer.getContext();
  context.setFont("Arial", 10, "").setBackgroundFillStyle("#eed");
  return { renderer, context };
}

export function drawScore(
  renderer,
  context,
  score,
  selectedNoteIndex,
  noteSelectedCallback,
  svgConfig,
  repeat,
) {
  const {
    width: svgWidthProposed,
    scale,
    hResize,
    vResize,
    justifyLastRow,
    measureNoteStartPadding = 0,
    measureNoteEndPadding = 0,
    measureGap = 0,
    minimumNoteSpacing = 0,
    maxMeasureWidth,
    measuresPerLine,
    hideTimeSignature = false,
    showMeasureNumbers = false,
    systemSpacing,
    // Unlike measuresPerLine, measures keep their natural widths: a row breaks
    // early when it is full and is then justified.
    maxMeasuresPerLine,
    partLabelsOnEveryRow = false,
    // Optional text above each measure, by measure index (e.g. BYOS letters).
    measureLabels,
  } = svgConfig;
  let { measures } = score;
  let systemWidth = 0;
  const shownParts = measures[0].parts.filter((part) => score.parts[part.instrument]?.enabled !== false).length, largeScore = shownParts >= LARGE_SCORE_PARTS;
  const hasDynamics = scoreHasDynamics(score), hasLyrics = scoreHasLyrics(score);
  LYRICS_BELOW_DYNAMICS = hasDynamics;
  // An editor keeps what it drew (svgConfig.keep, the same object each time): the rows that haven't
  // changed stay on the page, and bars already measured aren't measured again, so an edit costs a row,
  // not the whole score. Bars with takes draw an ossia of their own, so those scores draw in full.
  const keep = svgConfig.keep && !measures.some((measure) => measure.takes?.length > 1) ? svgConfig.keep : null;
  const measurePartsArray = new Array(measures.length).fill(null);
  const build = (indexes) => {
    const wanted = indexes && new Set(indexes.filter((index) => !measurePartsArray[index]));
    if (wanted && !wanted.size) return;
    getMeasureData(measures, score, { selectedNoteIndex, largeScore, showRange: Boolean(svgConfig.measureEditing), only: wanted }).forEach((parts, index) => {
      if (!parts) return;
      measurePartsArray[index] = parts;
      parts.forEach((part) => { part.label = partName(score, part.instrument); part.shortLabel = partName(score, part.instrument, { short: true }); });
    });
  };
  build(keep ? [0] : null);
  // Rows with pitched parts start with their clefs and the key each reads in; a key change mid-row
  // prints the new key (cancelling the old one). Both widen their measure.
  const hasClefs = measurePartsArray[0].some((part) => part.clef);
  const signatureWidths = new Map();
  const signatureWidth = (key, cancel, clef) => {
    const name = `${key}|${cancel}|${clef}`;
    if (!signatureWidths.has(name)) {
      const stave = new VF.Stave(0, 0, 300), before = stave.getNoteStartX();
      if (clef) stave.addClef(clef);
      if (key) stave.addKeySignature(key, cancel);
      signatureWidths.set(name, stave.getNoteStartX() - before);
    }
    return signatureWidths.get(name);
  };
  const rowStartExtra = (index) => hasClefs ? Math.max(0, ...measurePartsArray[index].filter((part) => part.clef).map((part) => signatureWidth(part.keySig, undefined, part.clef))) : 0;
  const keyChangeExtra = (index) => Math.max(0, ...measurePartsArray[index].filter((part) => part.keyChange).map((part) => signatureWidth(part.keySig, part.previousKey, false)));
  const configuredSystemSpacing = Number(systemSpacing);
  const baseSystemSpacing = Number.isFinite(configuredSystemSpacing) && configuredSystemSpacing > 0
    ? configuredSystemSpacing
    : BASE_STAVE_SPACE;
  const configuredMeasureGap = Number(measureGap);
  const effectiveMeasureGap = Number.isFinite(configuredMeasureGap) && configuredMeasureGap > 0
    ? configuredMeasureGap
    : 0;
  const configuredMinimumNoteSpacing = Number(minimumNoteSpacing);
  const effectiveMinimumNoteSpacing = Number.isFinite(configuredMinimumNoteSpacing) &&
    configuredMinimumNoteSpacing > 0
    ? configuredMinimumNoteSpacing
    : 0;
  const configuredMeasuresPerLine = Number.parseInt(measuresPerLine, 10);
  const effectiveMeasuresPerLine = Number.isInteger(configuredMeasuresPerLine) &&
    configuredMeasuresPerLine > 0
    ? configuredMeasuresPerLine
    : Number.POSITIVE_INFINITY;
  const dynamicsSpace = (hasDynamics ? DYNAMICS_SPACE : 0) + (hasLyrics ? LYRICS_SPACE : 0);
  PART_SPACE = BASE_STAVE_SPACE + dynamicsSpace;
  STAVE_SPACE = (baseSystemSpacing + dynamicsSpace) * measurePartsArray[0].length;
  PART_TOPS = measurePartsArray[0].map((_, index) => index * PART_SPACE);
  if (largeScore) {
    const extra = (hasDynamics ? LARGE_DYNAMICS_SPACE : 0) + (hasLyrics ? LARGE_LYRICS_SPACE : 0);
    let top = 0;
    PART_TOPS = measurePartsArray[0].map((part) => { const here = top; top += (part.lines === 1 ? LARGE_LINE_SPACE : LARGE_PART_SPACE) + extra; return here; });
    STAVE_SPACE = top + LARGE_ROW_MARGIN;
  }
  STAVE_TOP = scoreHasNestedTuplets(score) ? NESTED_TUPLET_HEADROOM : 0;
  TAKES = svgConfig.takes !== false && measures.some((measure) => measure.takes?.length > 1) ? { onTake: svgConfig.onTake || null, bars: [] } : null;
  if (TAKES) STAVE_SPACE += OSSIA_SPACE;
  const svgWidth = Math.max(svgWidthProposed, SCORE_MIN_WIDTH);
  const fixedMeasureWidth = Number.isFinite(effectiveMeasuresPerLine)
    ? (svgWidth - effectiveMeasureGap * (effectiveMeasuresPerLine - 1)) /
      effectiveMeasuresPerLine
    : null;
  const configuredMaxMeasuresPerLine = Number.parseInt(maxMeasuresPerLine, 10);
  const effectiveMaxMeasuresPerLine = configuredMaxMeasuresPerLine > 0
    ? configuredMaxMeasuresPerLine
    : Number.POSITIVE_INFINITY;
  // Room for the part names before a row's first measure: VexFlow sets them 24px off the staff.
  const labelled = [...new Set(measurePartsArray[0].map((part) => part.instrument))];
  const labelRoom = (short) => labelled.length > 1 ? Math.ceil(Math.max(...labelled.map((key) => partName(score, key, { short }).length)) * 9.4) + 30 : 0;
  LABEL_WIDTHS = [Math.max(labelled.length > 1 ? 100 : 0, labelRoom(false)), labelRoom(true)];
  const labelWidthFor = (index) => index === 0 ? LABEL_WIDTHS[0] : partLabelsOnEveryRow ? LABEL_WIDTHS[1] : 0;

  // What everything on the page depends on: when it changes, it's all drawn again.
  const signature = keep ? JSON.stringify([Object.entries(svgConfig).filter(([, value]) => ["string", "number", "boolean"].includes(typeof value)), svgWidth, largeScore, hasDynamics, hasLyrics, PART_TOPS, STAVE_SPACE, STAVE_TOP, LABEL_WIDTHS,
    score.parts, score.keySig, score.concert, measures[0].parts.map((part) => part.instrument), Boolean(svgConfig.measureEditing?.onPick)]) : null;
  if (svgConfig.keep && keep?.signature !== signature) {
    context.clear();
    Object.assign(svgConfig.keep, { signature, rows: [], parts: [], ties: null, sizes: new WeakMap() });
  }
  // Each bar's size depends on its notes, the key it's in, whether its time signature prints, and (in
  // a large score) which staff of it is being written in.
  const sizeKeys = keep ? measures.map((measure, index) => {
    const selected = selectedNoteIndex?.measureIndex === index ? `${selectedNoteIndex.partIndex}:${selectedNoteIndex.voiceIndex}` : "";
    return `${keyAt(score, index)}|${keyAt(score, index - 1)}|${_.isEqual(measures[index - 1]?.timeSig, measure.timeSig)}|${selected}`;
  }) : null;
  if (keep) build(measures.map((_measure, index) => index).filter((index) => keep.sizes.get(measures[index])?.key !== sizeKeys[index]));
  // Live callbacks: a row that stays was drawn with the ones of its day.
  const measureEditing = keep && svgConfig.measureEditing ? { ...svgConfig.measureEditing,
    onSelect: (...args) => keep.config.measureEditing.onSelect(...args), onOpen: (...args) => keep.config.measureEditing.onOpen(...args),
    onPick: svgConfig.measureEditing.onPick && ((...args) => keep.config.measureEditing.onPick?.(...args)) } : svgConfig.measureEditing;
  if (keep) keep.config = svgConfig;

  let barRenderData = [];
  let width = labelWidthFor(0);

  // With a measure cap, a row's measures share its width equally, as far as each
  // measure's notes allow. A short last row gets the measure width of a full row.
  function equalizeRow(isLastRow) {
    if (!Number.isFinite(effectiveMaxMeasuresPerLine)) return false;
    const labelWidth = labelWidthFor(row);
    const gaps = effectiveMeasureGap * (barRenderData.length - 1);
    const rowSpace = svgWidth - labelWidth - gaps;
    const fullRowBarWidth = (svgWidth - labelWidth - effectiveMeasureGap * (effectiveMaxMeasuresPerLine - 1)) / effectiveMaxMeasuresPerLine;
    const naturalWidth = barRenderData.reduce((sum, bar) => sum + bar.width, 0);
    const total = isLastRow ? Math.min(rowSpace, Math.max(naturalWidth, fullRowBarWidth * barRenderData.length)) : rowSpace;
    if (barRenderData.reduce((sum, bar) => sum + bar.minWidth, 0) > total) return false;
    equalizeRowWidths(barRenderData, total);
    width = labelWidth + gaps + total;
    return true;
  }
  let row = 0;
  let firstMeasure = true;
  let maxWidth = 0;
  const rows = [], measured = new Set();
  // A bar's natural width and the narrowest it can go.
  function sizeOf(measureParts, measureIndex) {
    let voices = measureParts.map((measurePart) => measurePart.voices);
    //One formatter for both parts so that we can calculate one width to
    //keep all the parts aligned.
    var formatter = new VF.Formatter();
    voices.map((v) => formatter.joinVoices(v));

    const opticalSpacingWeight = Math.max(
      0,
      ...measureParts.flatMap((measurePart) =>
        measurePart.opticalSpacingWeights || []
      )
    );
    const notationMinWidth = formatter.preCalculateMinTotalWidth(voices.flat());
    const rhythmMinWidth = Math.max(
      notationMinWidth,
      opticalSpacingWeight * effectiveMinimumNoteSpacing
    );
    let minTotalWidth = Math.ceil(Math.max(
      rhythmMinWidth,
      MIN_BAR_SIZE
    ));

    const timeSig = score.measures[measureIndex].timeSig;
    const sizingStave = new VF.Stave(0,0,100);
    if (!hideTimeSignature && !_.isEqual(score.measures[measureIndex-1]?.timeSig,timeSig)) sizingStave.addTimeSignature(`${timeSig.num}/${timeSig.type}`);
    const sized = score.measures[measureIndex];
    if (sized.repeatStart) sizingStave.setBegBarType(VF.Barline.type.REPEAT_BEGIN);
    if (sized.repeatEnd) sizingStave.setEndBarType(VF.Barline.type.REPEAT_END);
    const stavePadding = sizingStave.getNoteStartX() + 100 - sizingStave.getNoteEndX() + measureNoteStartPadding + measureNoteEndPadding;
    const minWidth = getTickContextsMinWidth(voices.flat()) + FIRST_NOTE_FORMAT_X + MEASURE_RIGHT_EDGE_GUARD + FORMAT_PADDING + stavePadding;
    systemWidth = minTotalWidth + FORMAT_PADDING + FIRST_NOTE_FORMAT_X + MEASURE_RIGHT_EDGE_GUARD + stavePadding;
    const naturalBarWidth = Math.max(systemWidth,minWidth);
    const configuredMaxMeasureWidth = Number(maxMeasureWidth);
    const naturalOrCappedBarWidth = Number.isFinite(configuredMaxMeasureWidth) && configuredMaxMeasureWidth > 0
      ? Math.min(naturalBarWidth, configuredMaxMeasureWidth)
      : naturalBarWidth;
    return { barWidth: fixedMeasureWidth || Math.max(naturalOrCappedBarWidth,minWidth), minWidth, rowExtra: rowStartExtra(measureIndex), changeExtra: keyChangeExtra(measureIndex) };
  }
  for (const [measureIndex, measureParts] of measurePartsArray.entries()) {
    let sized = keep?.sizes.get(measures[measureIndex]);
    if (!keep || sized?.key !== sizeKeys[measureIndex]) {
      sized = { ...sizeOf(measureParts, measureIndex), key: sizeKeys?.[measureIndex] };
      keep?.sizes.set(measures[measureIndex], sized);
      measured.add(measureIndex);
    }
    const { barWidth, minWidth } = sized;

    const gapBeforeMeasure = barRenderData.length ? effectiveMeasureGap : 0;

    if (
      barRenderData.length &&
      (
        barRenderData.length >= effectiveMeasuresPerLine ||
        barRenderData.length >= effectiveMaxMeasuresPerLine ||
        (!Number.isFinite(effectiveMeasuresPerLine) &&
          width + gapBeforeMeasure + barWidth > svgWidth)
      )
    ) {
      const remainingWidth = equalizeRow(false) ? 0 : Math.max(svgWidth - width, 0);
      if (fixedMeasureWidth) balanceRowWidths(barRenderData);
      rows.push({ bars: barRenderData, remainingWidth, row });

      if(width + remainingWidth > maxWidth) {
        maxWidth = width + remainingWidth
      }

      barRenderData = [];
      row += 1;
      width = labelWidthFor(row);
    }

    const appliedGap = barRenderData.length ? effectiveMeasureGap : 0;
    const startExtra = barRenderData.length ? sized.changeExtra : sized.rowExtra;

    barRenderData.push({
      measures: score.measures,
      parts: measureParts,
      width: barWidth + startExtra,
      // The narrowest stave that still holds every note and grace note.
      minWidth: minWidth + startExtra,
      firstMeasure,
      measureIndex,
      timeSig: score.measures[measureIndex].timeSig,
      measure: score.measures[measureIndex],
    });

    width += appliedGap + barWidth + startExtra;
    firstMeasure = false;
  }

  if (barRenderData.length) {
    const remainingWidth = equalizeRow(!justifyLastRow) ? 0 : justifyLastRow ? Math.max(svgWidth - width, 0) : 0;
    if (fixedMeasureWidth) balanceRowWidths(barRenderData);
    rows.push({ bars: barRenderData, remainingWidth, row });

    if(width + remainingWidth > maxWidth) {
      maxWidth = width + remainingWidth
    }
  }

  // Everything that shows in a row, besides its notes.
  const barKey = ({ measureIndex }) => JSON.stringify([measureIndex, sizeKeys[measureIndex],
    selectedNoteIndex?.measureIndex === measureIndex ? [selectedNoteIndex.partIndex, selectedNoteIndex.voiceIndex, selectedNoteIndex.noteIndex] : null,
    measureEditing?.selected === measureIndex, Boolean(measureEditing?.preview?.includes(measureIndex)), loopAt(repeat, measureIndex), loopAt(repeat, measureIndex - 1),
    measures[measureIndex - 1]?.ending, measures[measureIndex + 1]?.ending, measures[measureIndex - 1]?.timeSig]);
  for (const { bars, remainingWidth, row: index } of rows) {
    const frame = keep ? JSON.stringify([remainingWidth, bars.map((bar) => bar.width)]) : null, barKeys = keep ? bars.map(barKey) : null, held = keep?.rows[index];
    // With the row laid out as it was, only the bars that changed are drawn again.
    const same = held?.frame === frame && held.measures.length === bars.length;
    const changed = same ? bars.filter((bar, at) => bar.measure !== held.measures[at] || barKeys[at] !== held.barKeys[at]) : bars;
    if (same && !changed.length) continue;
    const partial = same && Boolean(measureEditing) && changed.length < bars.length;
    build(changed.map((bar) => bar.measureIndex));
    changed.forEach((bar) => {
      bar.parts = measurePartsArray[bar.measureIndex];
      // Grace notes are set up by measuring: a bar drawn again is measured again, even at a size that's known.
      if (!measured.has(bar.measureIndex)) sizeOf(bar.parts, bar.measureIndex);
    });
    const group = keep && !partial ? context.openGroup("score-row") : null;
    renderStaves(
      partial ? bars.map((bar) => changed.includes(bar) ? bar : { ...bar, skip: true }) : bars,
      remainingWidth,
      index,
      context,
      selectedNoteIndex,
      noteSelectedCallback,
      repeat,
      { ...measures[bars[0].measureIndex - 1]?.timeSig },
      measureNoteStartPadding,
      measureNoteEndPadding,
      hideTimeSignature,
      effectiveMeasureGap,
      showMeasureNumbers,
      measureEditing,
      partLabelsOnEveryRow,
      measureLabels,
      svgConfig.onKeySignature,
      svgConfig.onPart
    );
    if (keep) {
      if (group) context.closeGroup();
      // In its place, so the page reads in order.
      if (partial) changed.forEach((bar) => { const at = bars.indexOf(bar); held.groups[at].replaceWith(bar.group); held.groups[at] = bar.group; });
      else if (held) held.group.replaceWith(group);
      keep.rows[index] = { frame, barKeys, group: group || held.group, groups: partial ? held.groups : bars.map((bar) => bar.group), measures: bars.map((bar) => bar.measure) };
      changed.forEach((bar) => { keep.parts[bar.measureIndex] = bar.parts; });
    }
  }
  if (keep) {
    for (const stale of keep.rows.splice(rows.length)) stale.group.remove();
    keep.parts.length = measures.length;
    measurePartsArray.forEach((parts, index) => { measurePartsArray[index] = keep.parts[index]; });
    keep.ties?.remove();
    keep.ties = context.openGroup("ties");
  }
  drawTiesAndSlurs(context, measurePartsArray);
  if (keep) context.closeGroup();

  renderer.resize(
    (maxWidth + PADDING) * (hResize ?? 1),
    (STAVE_TOP + STAVE_SPACE * (row + 1)) * (vResize ?? 1) /** scale * scaleWidthMultipler*/
  );

  context.scale(scale, scale);
  // (Drawing an ossia draws a score of its own, so the bars are taken first.)
  const takes = TAKES;
  TAKES = null;
  if (takes) drawTakes(context, score, takes);
}

// The practice loop (playback only), drawn as a band over its measures. Books pass arrays.
function loopAt(repeat, index) {
  const set = typeof repeat.start === "number" || typeof repeat.end === "number";
  return set && index >= (typeof repeat.start === "number" ? repeat.start : 0) && index <= (typeof repeat.end === "number" ? repeat.end : Infinity);
}

// Each bar with takes: "Take 2 of 5" in its top corner (a button that shows the next one, where
// `onTake` is given), and that next take drawn over it, smaller, as an ossia.
function drawTakes(context, score, { bars, onTake }) {
  const add = (parent, tag, attributes, text) => {
    const element = document.createElementNS(SVG_NS, tag);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    if (text) element.textContent = text;
    parent.appendChild(element);
    return element;
  };
  for (const { measureIndex, x, y, width } of bars) {
    const measure = score.measures[measureIndex], shown = takeShown(measure), next = takesOf(measure).find((take) => take.id === nextTake(measure));
    if (!shown || !next) continue;
    // The next take: drawn on its own, off the page, then moved over the bar.
    const holder = document.createElement("div");
    holder.style.cssText = "position:absolute;left:-10000px;top:0;visibility:hidden";
    document.body.appendChild(holder);
    try {
      const renderer = new VF.Renderer(holder, VF.Renderer.Backends.SVG);
      drawScore(renderer, renderer.getContext(), { parts: score.parts, keySig: score.keySig, concert: score.concert, measures: [next.bar] }, null, () => {},
        { width: width / OSSIA_SCALE, scale: 1, hResize: 1, vResize: 1, hideTimeSignature: true, measuresPerLine: 1, takes: false }, {});
      const ossia = add(context.svg, "g", { transform: `translate(${x - (PADDING / 2) * OSSIA_SCALE},${y + 4}) scale(${OSSIA_SCALE})`, "data-ossia": String(measureIndex), "aria-hidden": "true" });
      [...holder.querySelector("svg").childNodes].forEach((child) => ossia.appendChild(child));
    } finally { holder.remove(); }
    add(context.svg, "text", { x: x + 4, y: y + 10, fill: "#c8193c", "font-size": 11, "font-family": "Arial", "data-ossia-label": String(measureIndex) },
      `Take ${takesOf(measure).findIndex((take) => take.id === next.id) + 1} (ossia)`);
    // Which take the bar shows.
    const label = `Take ${shown.number} of ${shown.count}`, badgeWidth = 12 + label.length * 6.3;
    const badge = add(context.svg, "g", { transform: `translate(${x + width - (onTake ? 52 : 8) - badgeWidth},${y + OSSIA_SPACE + 9})`, "data-take-badge": String(measureIndex) });
    add(badge, "rect", { width: badgeWidth, height: 20, rx: 10, fill: "#c8193c" });
    add(badge, "text", { x: badgeWidth / 2, y: 14, fill: "#fff", "font-size": 11.5, "font-weight": 600, "font-family": "Arial", "text-anchor": "middle" }, label);
    if (onTake) {
      Object.entries({ role: "button", tabindex: "0", "aria-label": `Bar ${measureIndex + 1}: ${label}. Show the next take` }).forEach(([name, value]) => badge.setAttribute(name, value));
      badge.style.cursor = "pointer";
      badge.addEventListener("click", (event) => { event.stopPropagation(); onTake(measureIndex); });
      badge.addEventListener("keydown", (event) => { if (["Enter", " "].includes(event.key)) { event.preventDefault(); event.stopPropagation(); onTake(measureIndex); } });
    }
  }
}

// The middle line of each clef, where rests and slashes sit, and the line a whole rest hangs from.
const MIDDLE_LINE = { treble: "b/4", bass: "d/3", alto: "c/4", tenor: "a/3", percussion: "b/4" };
const WHOLE_REST_LINE = { treble: "d/5", bass: "f/3", alto: "e/4", tenor: "c/4", percussion: "d/5" };
const OUT_OF_RANGE = { fillStyle: "#c8193c", strokeStyle: "#c8193c" };
const vexKey = (pitch) => { const { letter, accidental, octave } = parsePitch(pitch); return `${letter.toLowerCase()}${accidental}/${octave}`; };
// A note on a pitched staff, as its player reads it (`view`: see staffView), with the accidentals the
// measure needs (see measureAccidentals) and a dot on every key of a dotted chord. `outside` marks
// the notes its instrument can't play. `stem` is set when a staff has two voices: the first goes up
// and the second down, and their rests keep out of each other's way.
function getPitchedNote(note, clef, view, accidentals = [], outside = () => false, stem = null) {
  const keys = note.notes.length ? note.notes.map((pitch) => vexKey(view.written(pitch))) : [MIDDLE_LINE[clef]];
  const vfNote = new VF.StaveNote({ clef, keys, auto_stem: !stem && note.notes.length > 0, ...(stem ? { stem_direction: stem } : {}),
    duration: `${note.duration}${note.dots ? "d" : ""}${note.notes.length ? "" : "r"}` });
  if (stem && !note.notes.length) vfNote.setKeyLine(0, stem > 0 ? 3.5 : 0.5);
  accidentals.forEach((symbol, index) => { if (symbol) vfNote.addModifier(new VF.Accidental(symbol), index); });
  for (let dot = 0; dot < (note.dots || 0); dot++) keys.forEach((_, index) => vfNote.addModifier(new Dot(), index));
  note.notes.forEach((pitch, index) => { if (outside(pitch)) vfNote.setKeyStyle(index, OUT_OF_RANGE); });
  return vfNote;
}
// One whole rest in the middle of a silent bar, whatever its meter.
function getBarRest(clef, lines) {
  return new VF.StaveNote({ clef, keys: [lines === 1 ? MIDDLE_LINE[clef] : WHOLE_REST_LINE[clef]], duration: "wr", align_center: true });
}

// A note drawn as a slash on the staff's middle line, keeping its rhythm (rhythmic slashes).
function getSlashNote(note, clef) {
  const slash = new VF.StaveNote({ clef, keys: [MIDDLE_LINE[clef]], duration: `${note.duration}${note.dots ? "d" : ""}${note.notes.length ? "s" : "r"}` });
  for (let dot = 0; dot < (note.dots || 0); dot++) slash.addModifier(new Dot(), 0);
  return slash;
}
// One stemless slash per beat (dotted quarters in compound meters): "keep time".
function getBeatSlashes(timeSig, clef) {
  const compound = timeSig.type === 8 && timeSig.num % 3 === 0 && timeSig.num > 3;
  return Array.from({ length: compound ? timeSig.num / 3 : timeSig.num }, () => {
    const slash = new VF.StaveNote({ clef, keys: [MIDDLE_LINE[clef]], duration: `${compound ? timeSig.type / 2 : timeSig.type}${compound ? "d" : ""}s` });
    if (compound) slash.addModifier(new Dot(), 0);
    slash.hasStem = () => false;
    return slash;
  });
}
// A dynamic (mf, sfz…) on its own line below the staff and its stickings.
function getDynamicAnnotation(text) {
  return underStaff(new VF.Annotation(text).setFont("Times New Roman", 17, "bold", "italic"), DYNAMIC_BASELINE);
}
// A syllable of the lyrics, on its own line under the dynamics. A word's syllables end in a hyphen.
function getLyricAnnotation(text) {
  return underStaff(new VF.Annotation(text).setFont("Times New Roman", 15), DYNAMIC_BASELINE + (LYRICS_BELOW_DYNAMICS ? LYRIC_LINE : 0));
}
// An annotation on a baseline this far below the dynamics' line.
function underStaff(annotation, baseline) {
  annotation.setVerticalJustification(VF.Annotation.VerticalJustify.BOTTOM);
  annotation.draw = function draw() {
    const note = this.checkAttachedNote(), stave = note.checkStave();
    // VexFlow sets a bottom annotation's baseline its own glyph height down, which would put "p"
    // higher than "mf"; offset by that height so all dynamics share a baseline.
    const height = VF.TextFormatter.create(this.textFont).getYForStringInPx(this.text).height;
    const y = dynamicsLineY(stave, note) + baseline - height;
    note.getYs = () => [y - (this.text_line + 1) * VF.STAVE_LINE_DISTANCE];
    try { VF.Annotation.prototype.draw.call(this); } finally { delete note.getYs; }
  };
  return annotation;
}
// The baseline for dynamics and hairpins: a line below the stickings, which sit under the
// staff (or its lowest note).
function dynamicsLineY(stave, note) {
  const lines = note && !note.isRest?.() && note.getKeyProps ? note.getKeyProps().map((props) => props.line) : [];
  const lowestLine = Math.min(...lines, Number.isFinite(stave.lowestNoteLine) ? stave.lowestNoteLine : Infinity);
  const halfSpace = stave.getSpacingBetweenLines() / 2;
  return Math.max(stave.getYForLine(stave.getNumLines() - 1), Number.isFinite(lowestLine) ? stave.getYForNote(lowestLine) + halfSpace : -Infinity) + STICKING_GAP + 20;
}

// `only` builds just those measures; the others come back null.
function getMeasureData(measures, score, { selectedNoteIndex = null, largeScore = false, showRange = false, only = null } = {}) {
  const measurePartsArray = [], partConfig = score.parts, concert = Boolean(score.concert);
  let key = score.keySig || "C";

  measures.forEach((measure, measureIndex) => {
    let { parts, timeSig } = measure;
    // Pitched staffs follow key changes; the new key is printed where it changes.
    const previousKey = key;
    if (measure.keySig) key = measure.keySig;
    const keyChange = measureIndex > 0 && key !== previousKey;
    if (only && !only.has(measureIndex)) { measurePartsArray.push(null); return; }
    // A measure repeat (%) or beat slashes stand in for the notes; rhythmic slashes keep the rhythm.
    const standIn = measure.measureRepeat ? "repeat" : measure.slashes === "beat" ? "beats" : null;
    const slashed = measure.slashes === "rhythm";

    //Only render instruments that are enabled. Each note keeps its part's place in the measure.
    const shown = parts.map((part, partIndex) => ({ part, partIndex })).filter(({ part }) => partConfig[part.instrument]?.enabled !== false);
    const anyPitched = shown.some(({ part }) => instrumentOf(part.instrument)?.pitched);
    let measureParts = [];
    shown.forEach(({ part, partIndex }) => {
      const { voices, instrument } = part;
      const info = instrumentOf(instrument), pitched = Boolean(info?.pitched), drum = baseOf(instrument);
      // Each part reads in its own clef and key; a grand staff gives each voice (a piano's hands) a staff.
      const view = pitched ? staffView(instrument, key, { concert }) : null, previousView = pitched ? staffView(instrument, previousKey, { concert }) : null;
      const outside = showRange ? (pitch) => !inRange(instrument, pitch) : undefined;
      const staffs = pitched && info.staves > 1 ? voices.map((voice, voiceIndex) => [[voice, voiceIndex]]) : [voices.map((voice, voiceIndex) => [voice, voiceIndex])];
      staffs.forEach((staffVoicesIn, staffIndex) => {
      let staffVoices = staffVoicesIn;
      let vfVoices = [];
      let vfVoiceBeams = [];
      let vfVoiceNotes = [];
      let vfTuplets = [];
      let opticalSpacingWeights = [];
      const hairpins = [];
      const clef = pitched ? info.clefs[staffIndex] || "treble" : "percussion";
      // In a large score a silent bar is one whole rest, until it's being written in.
      const selectedHere = selectedNoteIndex?.measureIndex === measureIndex && selectedNoteIndex.partIndex === partIndex && staffVoices.some(([, voiceIndex]) => voiceIndex === selectedNoteIndex.voiceIndex);
      const barRest = largeScore && !standIn && !slashed && !selectedHere && staffVoices.every(([voice]) => voice.notes.every((note) => !note.notes.length));
      if (standIn || barRest) {
        // One stand-in voice for the staff; clicking it selects the measure's first note.
        const stand = barRest ? [getBarRest(clef, info?.lines)] : standIn === "repeat" ? [new VF.RepeatNote("1")] : getBeatSlashes(timeSig, clef);
        stand.forEach((n) => Object.assign(n, { noteIndex: 0, voiceIndex: staffVoices[0][1], partIndex, measureIndex, trueChopsPitches: "" }));
        const vfVoice = new VF.Voice({ num_beats: timeSig.num, beat_value: timeSig.type }).setMode(VF.Voice.Mode.SOFT);
        vfVoice.addTickables(stand);
        vfVoices.push(vfVoice);
        vfVoiceBeams.push([]);
        vfVoiceNotes.push(stand);
        opticalSpacingWeights.push(stand.length);
        staffVoices = [];
      }
      staffVoices.forEach(([voice, voiceIndex]) => {
        const { notes, tuplets } = voice;
        const voiceTuplets = Array.isArray(tuplets) ? tuplets : [];
        const stem = pitched && staffVoices.length > 1 ? (voiceIndex ? VF.Stem.DOWN : VF.Stem.UP) : null;
        const accidentals = pitched ? measureAccidentals(notes.map((note) => ({ notes: note.notes.map(view.written) })), view.key) : [];
        var vfNotes = [];
        notes.forEach((note, noteIndex) => {
          const n = slashed ? Object.assign(getSlashNote(note, clef), { trueChopsPitches: note.notes.join(" ") })
            : pitched ? Object.assign(getPitchedNote(note, clef, view, accidentals[noteIndex], outside, stem), { trueChopsPitches: note.notes.join(" "), trueChopsWritten: note.notes.map(view.written).join(" ") }) : getNote(
            VF.StaveNote.prototype.constructor,
            note,
            instrument
          );

          const numDots = pitched || slashed ? 0 : note.dots != null ? note.dots : 0;
          for (var i = 0; i < numDots; i++) {
            const dot = new Dot();
            n.addModifier(dot, i);
          }

          addOrnaments(note, n, drum, pitched && note.notes.length ? [vexKey(view.written(note.notes[0]))] : undefined);
          if (note.dynamic) n.addModifier(getDynamicAnnotation(note.dynamic), 0);
          if (note.text) n.addModifier(getTextAnnotation(note.text), 0);
          if (note.lyric) n.addModifier(getLyricAnnotation(note.lyric), 0);

          n.trueChopsNote = note;
          n.noteIndex = noteIndex;
          n.voiceIndex = voiceIndex;
          n.partIndex = partIndex;
          n.measureIndex = measureIndex;

          vfNotes.push(n);
        });

        opticalSpacingWeights.push(
          getVoiceOpticalSpacingWeight(notes, voiceTuplets)
        );

        voiceTuplets.forEach((tuplet) => {
          const tupletJsonNotes = notes.slice(tuplet.start, tuplet.end);
          // Standard groups (6:4, 7:4, 9:8) print just their count; only an
          // unusual span (4:3, 7:6) prints its ratio.
          const vfTuplet = new VF.Tuplet(vfNotes.slice(tuplet.start, tuplet.end), {
            num_notes: tuplet.actual,
            notes_occupied: tuplet.normal,
            bracketed: true,
            ratioed: !Number.isInteger(Math.log2(tuplet.normal)),
          });
          vfTuplet.trueChopsHasAccent = tupletJsonNotes.some((note) =>
            hasAboveStaffAccent(note, drum)
          );
          vfTuplets.push(vfTuplet);
        });

        // Create a voice in 4/4 and add the notes from above
        var vfVoice = new VF.Voice({
          num_beats: timeSig.num,
          beat_value: timeSig.type,
        });
        vfVoice.addTickables(vfNotes);
        vfVoices.push(vfVoice);
        const generatedBeams = getVoiceBeams(vfNotes, notes, voiceTuplets, timeSig, stem || pitched);
        vfTuplets.forEach((vfTuplet) => vfTuplet.setBracketed(true));
        vfVoiceBeams.push(generatedBeams);
        vfVoiceNotes.push(vfNotes);
        if (voice.hairpins?.length) hairpins.push({ hairpins: voice.hairpins, vfNotes });
      });

      measureParts.push({
        voices: vfVoices,
        notes: vfVoiceNotes,
        beams: vfVoiceBeams,
        tuplets: vfTuplets,
        opticalSpacingWeights,
        instrument,
        hairpins,
        partIndex,
        staffIndex,
        staves: staffs.length,
        lines: info?.lines,
        bracket: FAMILIES.find((family) => family.id === info?.family)?.bracket || null,
        // Drum parts print a clef only beside pitched ones, so the staffs line up.
        ...(pitched ? { clef, clefMark: info.clefMark, keySig: view.key, keyChange: keyChange && view.key !== previousView.key, previousKey: previousView.key }
          : anyPitched ? { clef: "percussion" } : {}),
      });
      });
    });

    measurePartsArray.push(measureParts);
  });

  return measurePartsArray;
}

function getVoiceOpticalSpacingWeight(notes, tuplets) {
  return notes.reduce((total, note, noteIndex) => {
    const duration = Number(note?.duration);
    if (!Number.isFinite(duration) || duration <= 0) {
      return total + 1;
    }

    const containingTuplet = tuplets.find((tuplet) =>
      noteIndex >= Number(tuplet.start) && noteIndex < Number(tuplet.end)
    );
    const tupletActual = Number(containingTuplet?.actual);
    const tupletNormal = Number(containingTuplet?.normal);
    const tupletDurationRatio = Number.isFinite(tupletActual) &&
      tupletActual > 0 &&
      Number.isFinite(tupletNormal) &&
      tupletNormal > 0
      ? tupletNormal / tupletActual
      : 1;
    const dotCount = Math.max(0, Number(note?.dots) || 0);
    let dotDurationRatio = 1;

    for (let dotIndex = 1; dotIndex <= dotCount; dotIndex += 1) {
      dotDurationRatio += 1 / (2 ** dotIndex);
    }

    const sixteenthDurationUnits = Math.max(
      (16 / duration) * tupletDurationRatio * dotDurationRatio,
      0.0625
    );
    return total + Math.sqrt(sixteenthDurationUnits);
  }, 0);
}

//Render the staves onto the score.
function renderStaves(
  barRenderData,
  remainingWidth,
  row,
  context,
  selectedNoteIndex,
  noteSelectedCallback,
  repeat,
  previousTimeSig,
  measureNoteStartPadding,
  measureNoteEndPadding,
  hideTimeSignature,
  measureGap,
  showMeasureNumbers,
  measureEditing,
  partLabelsOnEveryRow,
  measureLabels,
  onKeySignature,
  onPart
) {

  const barWidths = barRenderData.map((renderDataBar) => renderDataBar.width);
  const inLoop = (index) => loopAt(repeat, index);
  //Given the space left over in the stave (i.e.: remainingWidth), get the additional
  //width to add to each bar to make up that space.
  const additionalWidths = getAdditionalWidthsForBars(
    barWidths,
    remainingWidth
  );

  let x = PADDING / 2;
  const shown = barRenderData.find((bar) => !bar.skip).parts;
  const numParts = shown.length;
  const numInstruments = new Set(shown.map((part) => part.instrument)).size;

  const labelsHere = (row === 0 || partLabelsOnEveryRow) && numInstruments > 1;
  if (labelsHere) {
    x += LABEL_WIDTHS[row === 0 ? 0 : 1];
  }

  let staves = [];
  barRenderData.forEach((renderData, renderDataIndex) => {
    const { parts, width, firstMeasure, measureIndex, timeSig, measure = {} } = renderData;
    // A bar that stays as it's drawn: only its place is counted.
    if (renderData.skip) {
      Object.assign(previousTimeSig, timeSig);
      x += width + additionalWidths[renderDataIndex] + measureGap;
      return;
    }
    parts.forEach((part) => { part.row = row; });
    let xDiff = 0;
    let measureGroup;
    if (TAKES && measure.takes?.length > 1) TAKES.bars.push({ measureIndex, x, y: STAVE_TOP + row * STAVE_SPACE, width: width + additionalWidths[renderDataIndex] });
    if (measureEditing) {
      const group = renderData.group = measureGroup = context.openGroup("editable-measure");
      group.setAttribute("data-measure-index", measureIndex);
      const selected = measureEditing.selected === measureIndex, previewed = Boolean(measureEditing.preview?.includes(measureIndex));
      group.setAttribute("role", "button");
      group.setAttribute("tabindex", "0");
      group.setAttribute("aria-label", `Measure ${measureIndex + 1}`);
      group.setAttribute("aria-pressed", String(selected));
      group.setAttribute("data-selected", String(selected));
      if (previewed) group.setAttribute("data-idea", "true");
      group.style.cursor = "pointer";
      const background = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      Object.entries({ x: x + 2, y: STAVE_TOP + row * STAVE_SPACE + 2, width: width + additionalWidths[renderDataIndex] - 4,
        height: STAVE_SPACE - 4, rx: 9, fill: previewed ? "#f3eeff" : selected ? "#e8f3ff" : "transparent", stroke: previewed ? "#8a63e8" : selected ? "#4b9bff" : "transparent", "stroke-width": 2,
        "data-measure-background": "true" }).forEach(([name,value]) => background.setAttribute(name,value));
      group.appendChild(background);
      group.addEventListener("click", (event) => {
        if (notePressed) { notePressed = false; return; }
        if (event.target.closest(".vf-stavenote")) return;
        // Two clicks on a bar open its techniques. (The first redraws the score, so the browser's own
        // double-click never comes: they're counted here, real clicks only.)
        const now = Date.now();
        if (measureEditing.onPick && event.detail > 0) {
          watchPresses();
          if (lastBarClick.index === measureIndex && now - lastBarClick.at < 450) {
            lastBarClick = { index: null, at: 0 };
            measureEditing.onPick(measureIndex, { top: event.clientY + 8, left: event.clientX - 20 });
            return;
          }
          lastBarClick = { index: measureIndex, at: now };
        }
        measureEditing.onSelect(measureIndex);
      });
      group.addEventListener("keydown", (event) => {
        if (["Enter", " "].includes(event.key)) { event.preventDefault(); measureEditing.onSelect(measureIndex, true); }
      });
    }

    const measureStaves = parts.map((part, partIndex) => {
      let systemWidth = (width + additionalWidths[renderDataIndex]);
      const stave = new VF.Stave(
        x,
        STAVE_TOP + (TAKES ? OSSIA_SPACE : 0) + PART_TOPS[partIndex] + row * STAVE_SPACE,
        systemWidth,
        {
          space_above_staff_ln: 6,
        }
      );
      // A single sound's staff is one line: the middle one, where its notes are written.
      if (part.lines === 1) stave.setConfigForLines([false, false, true, false, false].map((visible) => ({ visible })));

      if (renderDataIndex === 0) {
        staves.push(stave);
      }

      if (measure.repeatStart) {
        stave.setBegBarType(VF.Barline.type.REPEAT_BEGIN);
      } else if (renderDataIndex > 0) {
        // Adjacent measures share the previous measure's ending barline.
        stave.setBegBarType(VF.Barline.type.NONE);
      }

      if (measure.repeatEnd) {
        stave.setEndBarType(VF.Barline.type.REPEAT_END);
      } else if (measure.barline === "double") {
        stave.setEndBarType(VF.Barline.type.DOUBLE);
      } else if (measure.barline === "final") {
        stave.setEndBarType(VF.Barline.type.END);
      }

      // Endings, signs, jumps and tempo changes go above the top staff only.
      if (partIndex === 0) {
        const span = endingSpan(renderData.measures || [], measureIndex);
        if (measure.ending?.length) {
          const type = { begin: "BEGIN", mid: "MID", end: "END", single: "BEGIN_END" }[span || "single"];
          // VexFlow measures these from far above the staff; bring them down under the measure number.
          stave.setVoltaType(VF.Volta.type[type], `${measure.ending.join(", ")}.`, 21);
        }
        if (measure.mark) stave.setRepetitionType(VF.Repetition.type[MARKS[measure.mark][0]], 25);
        if (measure.jump) stave.setRepetitionType(VF.Repetition.type[JUMPS[measure.jump][0]], 25);
      }

      const { voices, instrument } = part;
      // Stickings line up beneath the staff and its lowest note.
      stave.lowestNoteLine = Math.min(...voices.flatMap((voice) => voice.getTickables()
        .filter((note) => typeof note.getKeyProps === "function" && !note.isRest())
        .flatMap((note) => note.getKeyProps().map((props) => props.line))));

      // Each row of a pitched staff starts with its clef and the key in effect; a key change
      // prints the new key, cancelling the old one.
      if (part.clef && renderDataIndex === 0) {
        stave.addClef(part.clef, "default", part.clefMark || undefined);
        if (part.keySig) stave.addKeySignature(part.keySig);
      } else if (part.keyChange) {
        stave.addKeySignature(part.keySig, part.previousKey);
      }

      if (!hideTimeSignature && !_.isEqual(previousTimeSig, timeSig)) {
        stave.addTimeSignature(`${timeSig.num}/${timeSig.type}`);
      }

      if (firstMeasure || (partLabelsOnEveryRow && renderDataIndex === 0)) {
        //We don't need the measure label if it is the only instrument in the score.
        if (numInstruments > 1 && part.staffIndex === 0) {
          stave.setText(row === 0 ? part.label : part.shortLabel, Vex.Flow.Modifier.Position.LEFT, { shift_y: part.staves > 1 ? 16 : 0 });
        }

      }

      stave.setWidth(systemWidth);

      if (measureNoteStartPadding) {
        stave.setNoteStartX(stave.getNoteStartX() + measureNoteStartPadding);
      }
      return stave;
    });
    // Time signatures line up down the page, whatever clef and key each staff has.
    const signatures = measureStaves.length > 1 && !measureNoteStartPadding ? measureStaves.map((stave) => { stave.getNoteStartX(); return stave.getModifiers(VF.StaveModifierPosition.BEGIN, "TimeSignature")[0]; }) : [];
    const farthest = Math.max(...signatures.map((signature) => signature?.getX() ?? -Infinity));
    signatures.forEach((signature, index) => {
      if (!signature || signature.getX() >= farthest) return;
      signature.setPadding(signature.getPadding(2) + farthest - signature.getX());
      measureStaves[index].formatted = false;
    });
    const noteStartX = Math.max(...measureStaves.map((stave) => stave.getNoteStartX()));
    measureStaves.forEach((stave) => stave.setNoteStartX(noteStartX));

    parts.forEach((part, partIndex) => {
      const stave = measureStaves[partIndex];
      const systemWidth = width + additionalWidths[renderDataIndex];
      const { voices, notes, beams, tuplets } = part;
      var formatter = new VF.Formatter();
      stave.setContext(context).draw();
      if (partIndex === 0) drawMeasureText(context, stave, measure);
      if (part.keySig && onKeySignature && (renderDataIndex === 0 || part.keyChange)) addKeySignatureTarget(context, stave, part, measureIndex, onKeySignature);
      if (onPart && labelsHere && renderDataIndex === 0 && part.staffIndex === 0) addPartTarget(context, stave, part, LABEL_WIDTHS[row === 0 ? 0 : 1], onPart);

      if (measureLabels?.[measureIndex] && partIndex === 0) {
        context.save();
        context.setFont("Arial", 16, "bold");
        context.setFillStyle("#b71836");
        context.fillText(measureLabels[measureIndex], stave.getNoteStartX() + 2, stave.getYForLine(0) - 44);
        context.restore();
      }

      if (measureEditing && partIndex === 0) {
        context.save();
        context.setFont("Arial", 16, "");
        context.setFillStyle(measureEditing.selected === measureIndex ? "#2b6fd6" : "#8a8490");
        // Above the endings and signs, which sit between it and the staff.
        context.fillText(String(measureIndex + 1), stave.getX() + 6, stave.getYForLine(0) - 46);
        context.restore();
      }

      // Numbered from 1, or by a function of the measure's index (a pickup is bar 0).
      const measureNumber = typeof showMeasureNumbers === "function" ? showMeasureNumbers(measureIndex) : String(measureIndex + 1);
      if (showMeasureNumbers && measureNumber && partIndex === 0 && renderDataIndex === 0) {
        context.save();
        context.setFont("Times New Roman", MEASURE_NUMBER_FONT_SIZE, "bold");
        context.setFillStyle("#111111");
        const numberMetrics = context.measureText(measureNumber);
        const staffCenterY = (
          stave.getYForLine(0) +
          stave.getYForLine(stave.getNumLines() - 1)
        ) / 2;
        context.fillText(
          measureNumber,
          stave.getX() - MEASURE_NUMBER_GAP - numberMetrics.width,
          staffCenterY - numberMetrics.y - numberMetrics.height / 2
        );
        context.restore();
      }

      const availableNoteWidth = stave.getNoteEndX() - stave.getNoteStartX() - FORMAT_PADDING -
        measureNoteEndPadding;
      formatter.format(
        voices,
        Math.max(
          availableNoteWidth - FIRST_NOTE_FORMAT_X - MEASURE_RIGHT_EDGE_GUARD,
          1
        )
      );
      const firstNoteOffset = alignFirstNotePosition(voices);
      fitTickContextsToWidth(
        voices,
        availableNoteWidth - firstNoteOffset - MEASURE_RIGHT_EDGE_GUARD
      );

      xDiff = systemWidth;

      voices.map((vfVoice) => vfVoice.draw(context, stave));
      beams.map((vfBeams) =>
        vfBeams.map((beam) => beam.setContext(context).draw())
      );

      drawTuplets(tuplets, context);
      drawHairpins(context, stave, part.hairpins);
      
      notes.flat().forEach((note) => {
        // Pitched notes say which staff they're on, what they play and how they read.
        // Stand-ins (a measure repeat sign) may not draw their own group.
        if (!note.getSVGElement?.()) return;
        if (part.clef) {
          note.getSVGElement()?.setAttribute("data-staff", part.clef);
          note.getSVGElement()?.setAttribute("data-pitches", note.trueChopsPitches ?? "");
          if (note.trueChopsWritten) note.getSVGElement()?.setAttribute("data-written", note.trueChopsWritten);
        }
        // highlight the note if it selected
        if (
          selectedNoteIndex &&
          note.measureIndex === selectedNoteIndex.measureIndex &&
          note.partIndex === selectedNoteIndex.partIndex &&
          note.voiceIndex === selectedNoteIndex.voiceIndex &&
          note.noteIndex === selectedNoteIndex.noteIndex
        ) {
          note.getSVGElement().setAttribute('fill', NOTE_HIGHLIGHT_COLOR)
        }
        if (noteSelectedCallback) {
          const noteSvg = note.getSVGElement()
          const noteInteraction = new VexFlowInteraction(
            noteSvg,
            context.svg.createSVGPoint()
          );
          const events = ["touchStart"];
          events.forEach((type) => {
            noteInteraction.addEventListener(type, (e) => {
              //Two events are fired on mobile. One with e.type === 'mousedown' and another with
              //e.type === 'touchStart'. Desktop only fires e.type === 'mousedown'. Since we only want
              //this callback to fire once, we are calling out e.type === 'mousedown'.
              if (e.type === "mousedown") {
                notePressed = true;
                watchPresses();
                noteSelectedCallback(note, context);
              }
            });
          });
        }
      });
    });

    if (inLoop(measureIndex)) drawLoopBand(context, measureStaves[0], inLoop(measureIndex - 1));
    // Barlines run through each section of a score, and each grand staff.
    joinedStaffs(parts).forEach(([first, last]) => {
      new VF.StaveConnector(measureStaves[first], measureStaves[last]).setType(VF.StaveConnector.type.SINGLE_RIGHT).setContext(context).draw();
    });

    if (measureEditing?.onOpen) {
      // A "…" button in the measure's top-right corner opens its menu. The stylesheet
      // shows it on hover; right-click or the context-menu key opens the same menu.
      const trigger = context.openGroup("measure-trigger");
      trigger.setAttribute("data-measure-trigger", "true");
      trigger.setAttribute("aria-hidden", "true");
      trigger.setAttribute("transform", `translate(${x + xDiff - 44},${STAVE_TOP + (TAKES ? OSSIA_SPACE : 0) + row * STAVE_SPACE + 7})`);
      const add = (tag, attributes) => {
        const element = document.createElementNS("http://www.w3.org/2000/svg", tag);
        Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
        trigger.appendChild(element);
      };
      add("rect", { width: 38, height: 26, rx: 8, fill: "#fff", stroke: "#d9d4dd" });
      [12, 19, 26].forEach((cx) => add("circle", { cx, cy: 13, r: 2.2, fill: "#4d4753" }));
      const openBelowTrigger = () => {
        const box = trigger.getBoundingClientRect();
        measureEditing.onOpen(measureIndex, { top: box.bottom + 4, left: box.right, align: "right" });
      };
      trigger.addEventListener("click", (event) => { event.stopPropagation(); openBelowTrigger(); });
      measureGroup.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        if (event.clientX || event.clientY) measureEditing.onOpen(measureIndex, { top: event.clientY, left: event.clientX, align: "left" });
        else openBelowTrigger();
      });
      context.closeGroup();
    }
    if (measureEditing) context.closeGroup();
    previousTimeSig.num = timeSig.num;
    previousTimeSig.type = timeSig.type;
    x += xDiff + measureGap;
  });

  if (barRenderData.some((bar) => bar.skip)) return;
  if (numParts > 1) {
    var connector = new VF.StaveConnector(staves[0], staves[staves.length - 1]);
    connector.setType(VF.StaveConnector.type.SINGLE);
    connector.setContext(context);
    connector.draw();
  }
  // A brace joins a grand staff's two staves, and a bracket each section of a score.
  barRenderData[0].parts.forEach((part, index) => {
    if (part.staffIndex === 0 && part.staves > 1) {
      new VF.StaveConnector(staves[index], staves[index + 1]).setType(VF.StaveConnector.type.BRACE).setContext(context).draw();
    }
  });
  sectionRuns(barRenderData[0].parts).forEach(([first, last]) => {
    new VF.StaveConnector(staves[first], staves[last]).setType(VF.StaveConnector.type.BRACKET).setContext(context).draw();
  });
}

// Runs of two or more staffs of the same section: [[first, last]].
function sectionRuns(parts) {
  const runs = [];
  let first = 0;
  parts.forEach((part, index) => {
    if (index + 1 === parts.length || !part.bracket || part.bracket !== parts[index + 1].bracket) {
      if (part.bracket && index > first) runs.push([first, index]);
      first = index + 1;
    }
  });
  return runs;
}
// What barlines run through: sections, and grand staffs outside them.
function joinedStaffs(parts) {
  const runs = sectionRuns(parts);
  parts.forEach((part, index) => {
    if (part.staffIndex === 0 && part.staves > 1 && !runs.some(([first, last]) => index >= first && index + 1 <= last)) runs.push([index, index + 1]);
  });
  return runs;
}

// The ties and slurs of a whole score, drawn once every note has its place: over the barline, or
// in two pieces where a row ends between the notes.
function drawTiesAndSlurs(context, measurePartsArray) {
  const staffs = measurePartsArray[0]?.length || 0;
  for (let staff = 0; staff < staffs; staff++) {
    const voices = Math.max(...measurePartsArray.map((parts) => parts[staff]?.notes.length || 0));
    for (let voice = 0; voice < voices; voice++) {
      const notes = measurePartsArray.flatMap((parts) => (parts[staff]?.notes[voice] || []).filter((note) => note.trueChopsNote).map((vfNote) => ({ vfNote, note: vfNote.trueChopsNote, row: parts[staff].row })));
      notes.forEach(({ vfNote, note, row }, index) => {
        const next = notes[index + 1];
        if (note.tie && next?.note.notes.length && next.vfNote.measureIndex - vfNote.measureIndex <= 1) {
          const first_indices = note.notes.map((pitch, at) => at).filter((at) => next.note.notes.includes(note.notes[at])), last_indices = first_indices.map((at) => next.note.notes.indexOf(note.notes[at]));
          if (first_indices.length) {
            const pieces = row === next.row ? [{ first_note: vfNote, last_note: next.vfNote }] : [{ first_note: vfNote }, { last_note: next.vfNote }];
            for (const piece of pieces) new VF.StaveTie({ ...piece, first_indices, last_indices }).setContext(context).draw();
          }
        }
        if (note.slur === "start") {
          const after = notes.slice(index + 1), end = after.find((item) => item.note.slur)?.note.slur === "end" ? after.find((item) => item.note.slur) : after.find((item) => item.note.notes.length);
          if (!end) return;
          if (row === end.row) new VF.Curve(vfNote, end.vfNote).setContext(context).draw();
          else {
            const inRow = notes.filter((item) => item.row === row), inEndRow = notes.filter((item) => item.row === end.row);
            new VF.Curve(vfNote, inRow[inRow.length - 1].vfNote).setContext(context).draw();
            new VF.Curve(inEndRow[0].vfNote, end.vfNote).setContext(context).draw();
          }
        }
      });
    }
  }
}

// accel./rit. above the start of the measure, "3x" over a repeat that plays more than twice, a
// rehearsal mark in its box, and a tempo mark.
function drawMeasureText(context, stave, measure) {
  context.save();
  context.setFillStyle("#111111");
  const top = stave.getYForLine(0);
  if (measure.rehearsal) {
    const left = stave.getX() + 2, boxWidth = Math.max(20, 12 * String(measure.rehearsal).length + 8);
    context.setFont("Arial", 15, "bold");
    context.rect(left, top - 42, boxWidth, 22, { fill: "none", stroke: "#111111", "stroke-width": 1.4 });
    context.fillText(measure.rehearsal, left + 5, top - 25);
  }
  if (measure.tempo) {
    context.setFont("Times New Roman", 15, "bold");
    context.fillText(`\u2669 = ${measure.tempo}`, stave.getNoteStartX() + (measure.rehearsal ? 28 : 2), top - 24);
  }
  context.setFont("Times New Roman", 15, "bold", "italic");
  if (measure.tempoChange) context.fillText(TEMPO_CHANGES[measure.tempoChange], stave.getNoteStartX() + (measure.mark ? 24 : 2), stave.getYForLine(0) - 36);
  if (measure.repeatEnd && measure.repeatTimes) {
    const text = `${measure.repeatTimes}x`;
    context.setFont("Arial", 13, "bold");
    context.fillText(text, stave.getX() + stave.getWidth() - context.measureText(text).width - 4, stave.getYForLine(0) - 8);
  }
  context.restore();
}

// Crescendo and diminuendo wedges on the dynamics line.
const hasDynamic = (note) => note.getModifiers().some((modifier) => modifier.getCategory?.() === "Annotation" && /^[mpfrsz]+$/.test(modifier.text || ""));
function drawHairpins(context, stave, specs = []) {
  for (const { hairpins, vfNotes } of specs) for (const hairpin of hairpins) {
    // A wedge stops at the next dynamic inside it (mf < f, not through the f).
    const stop = vfNotes.findIndex((note, index) => index > hairpin.start && index < hairpin.end && hasDynamic(note));
    const end = stop > 0 ? stop : Math.min(hairpin.end, vfNotes.length);
    const first = vfNotes[hairpin.start], last = vfNotes[end - 1];
    if (!first || !last) continue;
    const y = Math.max(...vfNotes.slice(hairpin.start, end).map((note) => dynamicsLineY(stave, note))) + HAIRPIN_MIDDLE - HAIRPIN_HEIGHT / 2;
    const wedge = new VF.StaveHairpin({ first_note: first, last_note: last }, hairpin.type === "cresc" ? VF.StaveHairpin.type.CRESC : VF.StaveHairpin.type.DECRESC);
    wedge.setContext(context).setPosition(VF.Modifier.Position.BELOW);
    // A wedge starts after a dynamic on its first note, and runs to just before the next note
    // (or to the barline when it lasts to the end of the measure).
    const startsAfter = hasDynamic(first);
    const lastX = last.getModifierStartXY(VF.Modifier.Position.BELOW, 0).x;
    const endX = end < vfNotes.length ? vfNotes[end].getAbsoluteX() - 6 : stave.getNoteEndX() - 4;
    // VexFlow draws the wedge 20px below y_shift.
    wedge.setRenderOptions({ height: HAIRPIN_HEIGHT, y_shift: y - (stave.getY() + stave.getHeight()) - 20, left_shift_px: startsAfter ? 22 : 2,
      right_shift_px: Math.max(endX - lastX, 12), right_shift_ticks: 0, left_shift_ticks: 0 });
    const group = context.openGroup("hairpin");
    group.setAttribute("data-hairpin", hairpin.type);
    wedge.draw();
    context.closeGroup();
  }
}

// A clickable area over a piano staff's clef and key signature that opens the key picker.
function addKeySignatureTarget(context, stave, part, measureIndex, onKeySignature) {
  const group = context.openGroup("key-signature");
  const label = KEY_SIGNATURES.find((key) => key.spec === (part.keySig || "C"))?.label || part.keySig;
  const top = stave.getYForLine(0) - 6, height = stave.getYForLine(stave.getNumLines() - 1) - top + 6;
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  Object.entries({ x: stave.getX() + 2, y: top, width: Math.max(16, stave.getNoteStartX() - stave.getX() - 6), height, rx: 5, fill: "transparent", stroke: "none" })
    .forEach(([name, value]) => rect.setAttribute(name, value));
  group.appendChild(rect);
  group.setAttribute("data-key-signature", String(measureIndex));
  group.setAttribute("role", "button");
  group.setAttribute("tabindex", "0");
  group.setAttribute("aria-label", `Key signature: ${label}. Change key from measure ${measureIndex + 1}`);
  group.style.cursor = "pointer";
  const open = (event) => { event.stopPropagation(); event.preventDefault(); onKeySignature(measureIndex, group.getBoundingClientRect()); };
  group.addEventListener("mouseenter", () => rect.setAttribute("fill", "#b7183614"));
  group.addEventListener("mouseleave", () => rect.setAttribute("fill", "transparent"));
  group.addEventListener("mousedown", (event) => event.stopPropagation());
  group.addEventListener("click", open);
  group.addEventListener("keydown", (event) => { if (["Enter", " "].includes(event.key)) open(event); });
  context.closeGroup();
}

// A clickable area over a part's name on the score, which opens what can be done with the part.
function addPartTarget(context, stave, part, width, onPart) {
  const group = context.openGroup("part-label");
  const top = stave.getYForLine(0) - 8, height = stave.getYForLine(stave.getNumLines() - 1) - top + 8;
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  Object.entries({ x: stave.getX() - width + 2, y: top, width: width - 8, height, rx: 5, fill: "transparent", stroke: "none" }).forEach(([name, value]) => rect.setAttribute(name, value));
  group.appendChild(rect);
  group.setAttribute("data-part-label", part.instrument);
  group.setAttribute("role", "button");
  group.setAttribute("tabindex", "0");
  group.setAttribute("aria-label", `${part.label}: part options`);
  group.style.cursor = "pointer";
  const open = (event) => { event.stopPropagation(); event.preventDefault(); onPart(part.instrument, group.getBoundingClientRect()); };
  group.addEventListener("mouseenter", () => rect.setAttribute("fill", "#b7183614"));
  group.addEventListener("mouseleave", () => rect.setAttribute("fill", "transparent"));
  group.addEventListener("mousedown", (event) => event.stopPropagation());
  group.addEventListener("click", open);
  group.addEventListener("keydown", (event) => { if (["Enter", " "].includes(event.key)) open(event); });
  context.closeGroup();
}

// The practice loop: a band above the looped measures, labelled where it starts.
function drawLoopBand(context, stave, continues) {
  context.save();
  const y = stave.getYForLine(0) - 60;
  context.setFillStyle("#c8193f");
  context.setGlobalAlpha?.(0.35);
  context.fillRect(stave.getX() + 1, y, stave.getWidth() - 2, 4);
  context.setGlobalAlpha?.(1);
  if (!continues) {
    context.setFont("Arial", 11, "bold");
    context.fillText("Loop", stave.getX() + 26, y + 13);
  }
  context.restore();
}

function getAdditionalWidthsForBars(widths, remainingWidth) {
  const reducer = (sum, value) => sum + value;
  const totalWidth = widths.reduce(reducer, 0);
  const percentages = widths.map((width) => width / totalWidth);
  return percentages.map((percentage) => percentage * remainingWidth);
}

// How a meter's notes are beamed: as the editor's meters say, else by the beat (a dotted quarter
// in 6/8-like meters, an eighth in other x/8 and shorter, a quarter otherwise), so music in any
// meter can be drawn.
function beamGroups({ num, type }) {
  const known = timeSigs[`${num}/${type}`];
  if (known) return known.groups;
  return type >= 8 && num % 3 === 0 && num >= 6 ? [[3, type]] : [[1, Math.max(4, type)]];
}

function createAutomaticBeams(vfNotes, timeSig, pitched = false) {
  if (vfNotes.length < 2) {
    return [];
  }

  // Drum beams stay level rather than sloping with the notes or their beam counts, with
  // stems up; piano beams follow the melody and its stems point either way, unless `pitched` is a
  // stem direction: one of two voices on a staff.
  const stem = pitched === true ? null : pitched || Vex.Flow.StaveNote.STEM_UP;
  return VF.Beam.generateBeams(vfNotes, {
    beam_rests: false,
    show_stemlets: false,
    flat_beams: !pitched,
    ...(stem ? { stem_direction: stem } : {}),
    groups: beamGroups(timeSig).map(group =>
      new Vex.Flow.Fraction(group[0], group[1]))
  });
}

// Plain notes between tuplets are beamed by the beat they fall in, counted from
// the start of the measure, so a run that starts mid-beat (after a tuplet that
// starts or ends off the beat) still breaks at the real beat boundaries.
function createAutomaticBeamsFrom(vfNotes, startTicks, timeSig, pitched = false) {
  const groupTicks = beamGroups(timeSig)
    .map((group) => Vex.Flow.RESOLUTION * group[0] / group[1]);
  const boundaries = [];
  for (let tick = 0, index = 0; boundaries.length < 64; index += 1) {
    tick += groupTicks[index % groupTicks.length];
    boundaries.push(tick);
  }
  const chunks = new Map();
  let position = startTicks;
  vfNotes.forEach((note) => {
    const group = boundaries.findIndex((boundary) => position < boundary - 1e-6);
    chunks.set(group, [...(chunks.get(group) || []), note]);
    position += note.getTicks().value();
  });
  return [...chunks.values()].flatMap((chunk) => createAutomaticBeams(chunk, timeSig, pitched));
}

function hasQuarterOrLongerDuration(jsonNote) {
  return Number(jsonNote?.duration) <= LONGEST_UNBEAMED_TUPLET_DURATION;
}

function isJsonRest(jsonNote) {
  return !Array.isArray(jsonNote?.notes) || jsonNote.notes.length === 0;
}

// Every eighth or shorter note in a tuplet shares one beam, across rests;
// only quarter notes and longer values break it. `nested` holds the ranges of
// any groups nested inside, relative to the tuplet's first note.
function createTupletBeams(vfNotes, jsonNotes, pitched = false, nested = []) {
  const beams = [];
  let segmentStart = 0;

  while (segmentStart < jsonNotes.length) {
    while (segmentStart < jsonNotes.length && hasQuarterOrLongerDuration(jsonNotes[segmentStart])) {
      segmentStart += 1;
    }

    let segmentEnd = segmentStart;
    while (segmentEnd < jsonNotes.length && !hasQuarterOrLongerDuration(jsonNotes[segmentEnd])) {
      segmentEnd += 1;
    }

    let first = segmentStart;
    let last = segmentEnd - 1;
    while (first <= last && isJsonRest(jsonNotes[first])) first += 1;
    while (last >= first && isJsonRest(jsonNotes[last])) last -= 1;

    if (last > first) {
      // One beam over the played notes of the run; it spans any rests between them.
      const playedIndexes = Array.from({ length: last + 1 - first }, (_, offset) => first + offset)
        .filter((index) => !isJsonRest(jsonNotes[index]));
      const played = playedIndexes.map((index) => vfNotes[index]);
      if (pitched !== true) played.forEach((note) => note.setStemDirection(pitched || Vex.Flow.StaveNote.STEM_UP));
      const beam = new VF.Beam(played);
      beam.render_options.flat_beams = !pitched;
      // A partial beam at a nested group's edge points toward its own group's
      // notes (VexFlow otherwise points it left when its neighbours match).
      const groups = playedIndexes.map((index) => nested.findIndex((group) => index >= group.start && index < group.end));
      groups.forEach((group, offset) => {
        const withPrevious = groups[offset - 1] === group;
        const withNext = groups[offset + 1] === group;
        if (offset > 0 && offset < groups.length - 1 && withPrevious !== withNext) {
          beam.setPartialBeamSideAt(offset, withNext ? "R" : "L");
        }
      });
      beams.push(beam);
    }

    segmentStart = Math.max(segmentEnd, segmentStart + 1);
  }

  return beams;
}

function scoreHasLyrics(score) {
  return (score?.measures || []).some((measure) => (measure.parts || []).some((part) => (part.voices || []).some((voice) => (voice.notes || []).some((note) => note.lyric))));
}
function scoreHasDynamics(score) {
  return (score?.measures || []).some((measure) => (measure.parts || []).some((part) => (part.voices || []).some((voice) =>
    voice.hairpins?.length || (voice.notes || []).some((note) => note.dynamic))));
}

// A tuplet nested inside another shares its host's beam, so only top-level
// tuplets get beams of their own.
function scoreHasNestedTuplets(score) {
  return (score?.measures || []).some((measure) => (measure.parts || []).some((part) =>
    (part.voices || []).some((voice) => (voice.tuplets || []).some((tuplet) => isNestedTuplet(tuplet, voice.tuplets)))));
}

function isNestedTuplet(tuplet, tuplets) {
  return tuplets.some((other) => other !== tuplet &&
    Number(other.start) <= Number(tuplet.start) && Number(tuplet.end) <= Number(other.end) &&
    Number(other.end) - Number(other.start) > Number(tuplet.end) - Number(tuplet.start));
}

function getVoiceBeams(vfNotes, jsonNotes, tuplets, timeSig, pitched = false) {
  const forcedTupletRanges = [];
  const tupletBeams = [];

  tuplets
    .filter((tuplet) => !isNestedTuplet(tuplet, tuplets))
    .map((tuplet) => ({
      end: Math.min(Number(tuplet.end), vfNotes.length),
      start: Math.max(0, Number(tuplet.start)),
    }))
    .filter((tuplet) => Number.isInteger(tuplet.start) &&
      Number.isInteger(tuplet.end) &&
      tuplet.end > tuplet.start)
    .sort((left, right) => left.start - right.start)
    .forEach((tuplet) => {
      const tupletJsonNotes = jsonNotes.slice(tuplet.start, tuplet.end);

      const tupletVfNotes = vfNotes.slice(tuplet.start, tuplet.end);
      const nested = tuplets.filter((other) => isNestedTuplet(other, tuplets) &&
        Number(other.start) >= tuplet.start && Number(other.end) <= tuplet.end)
        .map((other) => ({ start: Number(other.start) - tuplet.start, end: Number(other.end) - tuplet.start }));
      const generatedTupletBeams = createTupletBeams(tupletVfNotes, tupletJsonNotes, pitched, nested);

      forcedTupletRanges.push(tuplet);

      if (!generatedTupletBeams.length) {
        return;
      }

      tupletBeams.push(...generatedTupletBeams);
    });

  if (!forcedTupletRanges.length) {
    return createAutomaticBeams(vfNotes, timeSig, pitched);
  }

  const automaticBeams = [];
  const startTicks = [];
  vfNotes.reduce((tick, note, index) => {
    startTicks[index] = tick;
    return tick + note.getTicks().value();
  }, 0);
  let cursor = 0;

  forcedTupletRanges.forEach((tuplet) => {
    if (tuplet.start > cursor) {
      automaticBeams.push(...createAutomaticBeamsFrom(vfNotes.slice(cursor, tuplet.start), startTicks[cursor], timeSig, pitched));
    }

    cursor = Math.max(cursor, tuplet.end);
  });

  if (cursor < vfNotes.length) {
    automaticBeams.push(...createAutomaticBeamsFrom(vfNotes.slice(cursor), startTicks[cursor], timeSig, pitched));
  }

  return [...automaticBeams, ...tupletBeams];
}

function areAdjacentTuplets(leftTuplet, rightTuplet) {
  const leftNotes = leftTuplet?.getNotes?.() || [];
  const rightNotes = rightTuplet?.getNotes?.() || [];
  const leftNote = leftNotes[leftNotes.length - 1];
  const rightNote = rightNotes[0];

  return leftNote &&
    rightNote &&
    leftNote.voiceIndex === rightNote.voiceIndex &&
    leftNote.noteIndex + 1 === rightNote.noteIndex;
}

function drawTuplet(vfTuplet, context, insetLeft, insetRight) {
  const tupletNotes = vfTuplet.getNotes();
  const firstNote = tupletNotes[0];
  const lastNote = tupletNotes[tupletNotes.length - 1];
  const originalGetYPosition = vfTuplet.getYPosition;
  const originalGetTieLeftX = firstNote.getTieLeftX;
  const originalGetTieRightX = lastNote.getTieRightX;
  const originalFillRect = context.fillRect;
  const yOffset = vfTuplet.trueChopsHasAccent
    ? ACCENTED_TUPLET_NOTEWARD_OFFSET
    : UNACCENTED_TUPLET_NOTEWARD_OFFSET;

  vfTuplet.getYPosition = function getNotewardTupletYPosition() {
    return originalGetYPosition.call(this) + yOffset;
  };

  if (insetLeft) {
    firstNote.getTieLeftX = function getInsetTupletTieLeftX() {
      return originalGetTieLeftX.call(this) + TUPLET_ADJACENT_BRACKET_INSET;
    };
  }

  if (insetRight) {
    lastNote.getTieRightX = function getInsetTupletTieRightX() {
      return originalGetTieRightX.call(this) - TUPLET_ADJACENT_BRACKET_INSET;
    };
  }

  context.fillRect = function fillTupletBracketRect(x, y, width, height) {
    const shortenedHeight = width === 1 && height === 10
      ? TUPLET_BRACKET_TICK_HEIGHT
      : height;
    return originalFillRect.call(this, x, y, width, shortenedHeight);
  };

  try {
    vfTuplet.setContext(context).draw();
  } finally {
    context.fillRect = originalFillRect;
    vfTuplet.getYPosition = originalGetYPosition;
    firstNote.getTieLeftX = originalGetTieLeftX;
    lastNote.getTieRightX = originalGetTieRightX;
  }
}

function drawTuplets(tuplets, context) {
  if (!tuplets.length) {
    return;
  }

  tuplets.forEach((vfTuplet, index) => {
    drawTuplet(
      vfTuplet,
      context,
      index > 0 && areAdjacentTuplets(tuplets[index - 1], vfTuplet),
      index < tuplets.length - 1 && areAdjacentTuplets(vfTuplet, tuplets[index + 1])
    );
  });
}

function alignFirstNotePosition(voices) {
  const tickContexts = new Set();

  voices.forEach((voice) => {
    voice.getTickables().forEach((tickable) => {
      const tickContext = tickable.getTickContext();
      if (tickContext) {
        tickContexts.add(tickContext);
      }
    });
  });

  const orderedContexts = [...tickContexts].sort((left, right) => left.getCurrentTick().value() - right.getCurrentTick().value());
  const firstContext = orderedContexts[0];
  if (!firstContext) {
    return FIRST_NOTE_FORMAT_X;
  }

  const firstNoteOffset = FIRST_NOTE_FORMAT_X + firstContext.getMetrics().totalLeftPx;
  const xShift = firstNoteOffset - firstContext.getX();
  orderedContexts.forEach((tickContext) => {
    tickContext.setX(tickContext.getX() + xShift);
  });
  return firstNoteOffset;
}

// With a fixed number of measures per line, a crowded measure borrows width from
// roomier measures in its row; the row's total width is unchanged.
function balanceRowWidths(bars) {
  if (bars.every((bar) => bar.minWidth <= bar.width)) return;
  const total = bars.reduce((sum, bar) => sum + bar.width, 0);
  if (bars.reduce((sum, bar) => sum + bar.minWidth, 0) > total) return;
  equalizeRowWidths(bars, total);
}

// Splits total equally, raising crowded measures to their minimum width.
function equalizeRowWidths(bars, total) {
  const atMinimum = new Set();
  for (;;) {
    const others = bars.filter((bar) => !atMinimum.has(bar));
    const share = (total - [...atMinimum].reduce((sum, bar) => sum + bar.minWidth, 0)) / others.length;
    const crowded = others.filter((bar) => bar.minWidth > share);
    if (!crowded.length) {
      bars.forEach((bar) => { bar.width = atMinimum.has(bar) ? bar.minWidth : share; });
      return;
    }
    crowded.forEach((bar) => atMinimum.add(bar));
  }
}

// VexFlow spaces dense measures by their widest note, so many grace notes push the
// measure past its stave. Give each note only the room it needs and share the rest
// in the formatter's rhythmic proportions.
function getTickContextLayout(voices) {
  const contexts = [...new Set(voices.flatMap((voice) =>
    voice.getTickables().map((tickable) => tickable.getTickContext()).filter(Boolean)
  ))].sort((left, right) => left.getCurrentTick().value() - right.getCurrentTick().value());
  const metrics = contexts.map((context) => context.getMetrics());
  const rightEdge = (index) => metrics[index].notePx + metrics[index].totalRightPx;
  // Room each note needs after the previous one: its notehead, modifiers, and grace notes.
  const needs = contexts.slice(1).map((_, index) =>
    rightEdge(index) + metrics[index + 1].totalLeftPx + TICK_CONTEXT_MIN_GAP
  );
  return { contexts, needs, firstLeftEdge: metrics[0]?.totalLeftPx || 0, lastRightEdge: contexts.length ? rightEdge(contexts.length - 1) : 0 };
}

function getTickContextsMinWidth(voices) {
  const { needs, firstLeftEdge, lastRightEdge } = getTickContextLayout(voices);
  return firstLeftEdge + needs.reduce((sum, need) => sum + need, lastRightEdge);
}

function fitTickContextsToWidth(voices, width) {
  if (!Number.isFinite(width)) return;
  const { contexts, needs, lastRightEdge } = getTickContextLayout(voices);
  if (contexts.length < 2) return;

  const start = contexts[0].getX();
  const last = contexts.length - 1;
  if (contexts[last].getX() + lastRightEdge - start <= width) return;

  const gaps = contexts.slice(1).map((context, index) => context.getX() - contexts[index].getX());
  const totalGap = gaps.reduce((sum, gap) => sum + gap, 0);
  const slack = Math.max(
    0,
    width - lastRightEdge - needs.reduce((sum, need) => sum + need, 0)
  );
  let x = start;
  gaps.forEach((gap, index) => {
    x += needs[index] + (totalGap > 0 ? slack * gap / totalGap : 0);
    contexts[index + 1].setX(x);
  });
}

function hasAboveStaffAccent(jsonNote, instrument) {
  const ornaments = String(jsonNote?.ornaments || "");

  return Boolean(ornaments) && (
    ornaments.includes(ACCENT) ||
    (instrument === "snare" && jsonNote?.notes?.includes("E5"))
  );
}

function getStickingAnnotation(text) {
  const annotation = new VF.Annotation(text);
  const setTextLine = annotation.setTextLine.bind(annotation);

  annotation.setVerticalJustification(VF.Annotation.VerticalJustify.BOTTOM);
  annotation.setFont("Times New Roman", 12, "normal");
  // Keep stickings on a dedicated line beneath the staff without allowing
  // VexFlow to push them past the system boundary and clip the final row.
  annotation.setTextLine = () => setTextLine(1.7);

  // VexFlow sets bottom text a fixed distance below its own note, so stickings
  // under high noteheads (rims, clicks) would climb into the staff. Instead, put
  // every sticking on one line under the staff, lower only if a note in the
  // staff hangs below it.
  annotation.draw = function draw() {
    const note = this.checkAttachedNote(), stave = note.checkStave();
    const noteLines = note.getKeyProps().map((props) => props.line);
    const lowestLine = Math.min(...noteLines, Number.isFinite(stave.lowestNoteLine) ? stave.lowestNoteLine : Infinity);
    const halfSpace = stave.getSpacingBetweenLines() / 2;
    const textTop = Math.max(stave.getYForLine(stave.getNumLines() - 1), stave.getYForNote(lowestLine) + halfSpace) + STICKING_GAP;
    // VexFlow draws at (lowest note y + (text line + 1) line distances), so
    // report the y that lands the text at textTop.
    note.getYs = () => [textTop - (this.text_line + 1) * VF.STAVE_LINE_DISTANCE];
    try { VF.Annotation.prototype.draw.call(this); } finally { delete note.getYs; }
  };

  return annotation;
}

// Words over a note: pizz., con sord., solo.
function getTextAnnotation(text) {
  return new VF.Annotation(text).setFont("Times New Roman", 14, "normal", "italic").setVerticalJustification(VF.Annotation.VerticalJustify.TOP).setJustification(VF.Annotation.HorizontalJustify.LEFT);
}
// VexFlow's sign for each articulation, and whether it sits at the notehead (else above the staff).
const ARTICULATIONS = [[STACCATO, "a.", true], [TENUTO, "a-", true], [MARCATO, "a^"], [FERMATA, "a@a"], [DOWN_BOW, "am"], [UP_BOW, "a|"], [OPEN, "ah"], [STOPPED, "a+"]];

function addOrnaments(jsonNote, scoreNote, instrument, graceKeys) {
  if (jsonNote.ornaments) {
    for (const [letter, code, atHead] of ARTICULATIONS) {
      if (jsonNote.ornaments.includes(letter)) scoreNote.addModifier(new VF.Articulation(code).setPosition(atHead && scoreNote.getStemDirection() === VF.Stem.UP ? VF.Modifier.Position.BELOW : VF.Modifier.Position.ABOVE), 0);
    }
    if (jsonNote.ornaments.includes(CHEESE)) {
      scoreNote.addModifier(new VF.Tremolo(1), 0);

      scoreNote.addModifier(
        new VF.GraceNoteGroup([
          getGraceNote(VF.GraceNote.prototype.constructor, graceKeys),
        ]), 0
      );
    }

    //diddle - add tremolo
    if (jsonNote.ornaments.includes(DIDDLE)) {
      scoreNote.addModifier(new VF.Tremolo(1), 0);
    } else if (jsonNote.ornaments.includes(BUZZ)) {
      scoreNote.addModifier(new VF.Tremolo(3), 0);
    }

    //flam - add grace note
    if (jsonNote.ornaments.includes(FLAM)) {
      scoreNote.addModifier(
        new VF.GraceNoteGroup([
          getGraceNote(VF.GraceNote.prototype.constructor, graceKeys),
        ]) ,0
      );
    }

    if(jsonNote.notes.indexOf('E5') >= 0 && instrument == 'snare') {
      scoreNote.addModifier(new VF.Articulation("a^").setPosition(3), 0);
    } else if (jsonNote.ornaments.includes(ACCENT)) {
      scoreNote.addModifier(new VF.Articulation("a>").setPosition(3), 0);
    }

    //right sticking - add 'R' annotation
    if (jsonNote.ornaments.includes(RIGHT_STICKING)) {
      scoreNote.addModifier(getStickingAnnotation("R"), 0);
    } else if (jsonNote.ornaments.includes(LEFT_STICKING)) {
      //left sticking - add 'L' annotation
      scoreNote.addModifier(getStickingAnnotation("L"), 0);
    }
  }
}
